import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from "react-router-dom";
import { Layout, Card, Typography, notification, Empty, Space, Divider, Tabs } from 'antd';
import { Button, ButtonGroup } from "@mui/material";
import { ArrowBackIosNew, ThumbUpAltRounded } from "@mui/icons-material";

import PageHeader from "../../../layouts/PageHeader";
import PortfolioSignalsTable from "./PortfolioSignalsTable";
import ThirdPartyExports from "../ThirdPartyExports";
import { fetchDetailedPortfolioObjects, saveUserPortfolioSnapshot, fetchUserPortfolioHistory } from '../../../api/portfolioApi';
import { selectFullPortfolios } from "../../../store/slices/portfolios.slice";
import { toDateTime } from "../../../utils/datetime";

const { Content, Sider } = Layout;
const { Title, Text } = Typography;

const formatWeightsForSubmission = (dataSource) => {
  return dataSource.map(({ id, weight }) => ({
    id,
    weight: parseFloat(weight),
  }));
};

const formatPortfolioForComparison = (currentSymbols, historySymbols = []) => {
  const historyIndex = historySymbols.reduce((acc, cur) => {
    acc[cur.id] = cur;
    return acc;
  }, {});

  return Object.entries(currentSymbols).map(([id, weight]) => {
    const currentWeight = parseFloat(weight);
    const historyWeight = historyIndex[id]?.weight || 0;
    const delta = currentWeight - historyWeight;
    return {
      id,
      weight: currentWeight,
      delta,
    };
  });
};

const PortfolioSignals = () => {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const detailedPortfolio = useSelector(state =>
    selectFullPortfolios(state, [portfolioId])
  )[0];

  const [initialDataSource, setInitialDataSource] = useState([]);
  const [historyDataSource, setHistoryDataSource] = useState([]);
  const [historyTimestamp, setHistoryTimestamp] = useState(null);

  const [loadingPortfolio, setLoadingPortfolio] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [showSider, setShowSider] = useState(true);
  const [showExport, setShowExport] = useState(false);

  useEffect(() => {
    if (portfolioId) {
      setLoadingPortfolio(true);
      setLoadingHistory(true);

      // Fetch detailed portfolio
      dispatch(fetchDetailedPortfolioObjects([portfolioId]))
        .then(() => setLoadingPortfolio(false))
        .catch(error => {
          notification.error({
            message: 'Error Fetching Portfolio',
            description: `There was an error fetching the portfolio details: ${error}`
          });
        });

      // Fetch history
      dispatch(fetchUserPortfolioHistory({ portfolioId }))
        .then((history) => {
          if (history.payload && history.payload.historyData.length > 0) {
            const historyData = history.payload.historyData[0].snapshot;
            const timestamp = toDateTime(history.payload.historyData[0].timestamp);
            setHistoryDataSource(historyData);
            setHistoryTimestamp(timestamp);
          }
          setLoadingHistory(false);
        })
        .catch(error => {
          notification.error({
            message: 'Error Fetching History',
            description: `There was an error fetching the portfolio history: ${error}`
          });
        });
    }
  }, [dispatch, portfolioId]);

  useEffect(() => {
    if (!loadingPortfolio && !loadingHistory && detailedPortfolio?.symbols) {
      setInitialDataSource(
        formatPortfolioForComparison(detailedPortfolio.symbols, historyDataSource)
      );
    }
  }, [loadingPortfolio, loadingHistory, detailedPortfolio, historyDataSource]);

  const handlePortfolioSubmit = () => {
    dispatch(
      saveUserPortfolioSnapshot({
        portfolioID: portfolioId,
        snapshot: formatWeightsForSubmission(initialDataSource),
      })
    )
      .then(() => {
        notification.success({
          message: 'Portfolio Updated',
          description: 'The portfolio has been successfully updated.'
        });
      })
      .catch(error => {
        notification.error({
          message: 'Error Updating Portfolio',
          description: `There was an error updating the portfolio: ${error}`
        });
      });
  };

  const renderCurrentTab = () => {
    if (!initialDataSource.length) {
      return <Empty description="No portfolio data available." />;
    }
    return (
      <PortfolioSignalsTable
        dataSource={initialDataSource.sort((a, b) => b.weight - a.weight)}
        title={<Title level={3}>Current Portfolio</Title>}
      />
    );
  };

  const renderHistoryTab = () => {
    if (!historyDataSource.length) {
      return <Empty description="No portfolio history available." />;
    }
    return (
      <PortfolioSignalsTable
        dataSource={historyDataSource}
        showDelta={false}
        allowExport={false}
        title={
          <Title level={4} style={{ color: 'red' }}>
            {`Portfolio on ${historyTimestamp}`}
          </Title>
        }
        showExport={showExport}
        setShowExport={setShowExport}
      />
    );
  };

  return (
    <>
      <PageHeader
        title={detailedPortfolio?.name || "Portfolio"}
        actions={[
          !showSider && (
            <Button
              variant="contained"
              key="show"
              onClick={() => setShowSider(true)}
            >
              Show Side Panel
            </Button>
          )
        ]}
      />
      <Layout style={{ height: '100vh', overflow: 'auto' }}>
        {showSider && (
          <Sider width={400} className={'bg-transparent'}>
            <Card
              title={
                <ButtonGroup color="inherit" size="small" variant="outlined">
                  <Button
                    key="back"
                    size="small"
                    onClick={() => navigate(-1)}
                    startIcon={<ArrowBackIosNew />}
                  >
                    Back
                  </Button>
                  <Button onClick={() => setShowSider(false)}>Hide Side</Button>
                </ButtonGroup>
              }
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Title level={4}>Confirm Signals</Title>
                <Text>
                  Please review the signals below, then acknowledge to save the portfolio state.
                </Text>
                <Button
                  variant="contained"
                  startIcon={<ThumbUpAltRounded />}
                  onClick={handlePortfolioSubmit}
                >
                  Acknowledge
                </Button>
              </Space>
              <Divider />
              <Title level={4}>Export Options</Title>
              <Divider />
              <Space>
                <Button onClick={() => setShowExport(true)}>Export to CSV</Button>
                <Button>Export to 3rd Party</Button>
              </Space>
              <ThirdPartyExports mode="straight" dataSource={initialDataSource} />
            </Card>
          </Sider>
        )}
        <Content style={{ padding: '1rem' }}>
          <Tabs defaultActiveKey="current">
            <Tabs.TabPane tab="Current" key="current">
              {renderCurrentTab()}
            </Tabs.TabPane>
            <Tabs.TabPane tab="History" key="history">
              {renderHistoryTab()}
            </Tabs.TabPane>
          </Tabs>
        </Content>
      </Layout>
    </>
  );
};

export default PortfolioSignals;
